import { useMutation, useQueryClient } from 'react-query';
import { COOKIES_CONFIG } from '@belong/common';
import { shutdownOneLiveChat } from '@belong/integrations-belong-one-chat/utils';
import { parseCookies, setCookie, destroyCookie } from 'nookies';
import { startOTPFlowService, verifyOTPCodeService } from 'src/api/auth';
import type {
  ExternalLoginModel,
  LoginRecaptchaUserModel,
  RegisterUserModel,
} from 'src/api/models';
import {
  loginExternalService,
  loginService,
  registerService,
  forgotPasswordService,
  logoutService,
  resetPasswordService,
  setPasswordService,
} from 'src/api/users';
import { setJwtToken, clearJwtToken } from 'src/utils/api-client';
import {
  setJwtToken as setOctaneJwtToken,
  clearJwtToken as clearOctaneJwtToken,
} from 'src/utils/octane-client';
import { setEnhancedConvertionData } from 'src/utils/tracking';

const { JWT_TOKEN, REFRESH_TOKEN, PARTNER_KEY, REFERRAL_USER, REFERRAL_ID } =
  COOKIES_CONFIG;

export type LoginProviders = 'Facebook' | 'LinkedIn' | 'Google' | 'Apple';

export type UserLogin = {
  email: string;
  password: string;
};

export type ForgotPasswordEmail = {
  email: string;
};

// `satisfies`, not an annotation: a `Record<string, …>` discards the three
// keys written right below it, so every lookup came back `| undefined` and the
// external-login requests built from it stopped matching `ExternalLoginModel`.
export const LoginProvider = {
  Facebook: 'Facebook',
  LinkedIn: 'LinkedIn',
  Google: 'Google',
} satisfies Record<string, LoginProviders>;

export function useLogin() {
  const { isLoading, mutateAsync } = useMutation(login);

  return {
    isLoginLoading: isLoading,
    login: mutateAsync,
  };
}

export function useExternalLogin() {
  const { isLoading, mutateAsync } = useMutation(externalLogin());

  return {
    isExternalLoginLoading: isLoading,
    externalLogin: mutateAsync,
  };
}

export function useRegister() {
  const { isLoading, mutateAsync } = useMutation(register);

  return {
    isRegisterLoading: isLoading,
    register: mutateAsync,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation(logout, {
    onSuccess: () => {
      queryClient.setQueryData('user', undefined);
    },
  });
}

export function useForgotPassword() {
  const queryClient = useQueryClient();

  return useMutation(forgotPasswordService, {
    onSuccess: () => {
      queryClient.invalidateQueries('user');
    },
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();

  return useMutation(resetPasswordService, {
    onSuccess: () => {
      queryClient.invalidateQueries('user');
    },
  });
}

export function useSetPassword() {
  return useMutation(setPasswordService);
}

async function register(registerData: RegisterUserModel) {
  const cookies = parseCookies();
  const partnerKey = cookies[PARTNER_KEY.name];

  const data = await registerService({
    ...registerData,
    encodedPartnerKey: partnerKey,
  });

  if (data.token) {
    setJwtToken(data.token);
    setOctaneJwtToken(data.token);
    setCookie(null, JWT_TOKEN.name, data.token, JWT_TOKEN.options);
    if (data.refreshToken) {
      setCookie(
        null,
        REFRESH_TOKEN.name,
        data.refreshToken,
        REFRESH_TOKEN.options,
      );
    }

    if (registerData?.email && window.mixpanel) {
      window.mixpanel.register({
        email: registerData.email,
        firstName: registerData.firstName,
        lastName: registerData.lastName,
        phone: registerData.phone,
        registrationSource: 'belong-next',
        registrationTimestamp: new Date().toISOString(),
      });
    }
  }

  return data;
}

async function login(user: LoginRecaptchaUserModel) {
  const { token, refreshToken } = await loginService(user);

  if (!token) {
    throw new Error();
  }

  setEnhancedConvertionData(user);
  setCookie(null, JWT_TOKEN.name, token, JWT_TOKEN.options);
  if (refreshToken) {
    setCookie(null, REFRESH_TOKEN.name, refreshToken, REFRESH_TOKEN.options);
  }
  setJwtToken(token);
  setOctaneJwtToken(token);

  if (user?.email && window.mixpanel) {
    window.mixpanel.register({
      email: user.email,
      loginSource: 'belong-next',
      loginTimestamp: new Date().toISOString(),
    });
  }
}

function externalLogin() {
  return async function (user: ExternalLoginModel) {
    const { token, refreshToken } = await loginExternalService(user);

    if (!token) {
      throw new Error();
    }

    setEnhancedConvertionData(user);
    setCookie(null, JWT_TOKEN.name, token, JWT_TOKEN.options);
    if (refreshToken) {
      setCookie(null, REFRESH_TOKEN.name, refreshToken, REFRESH_TOKEN.options);
    }
    setJwtToken(token);
    setOctaneJwtToken(token);

    if (user?.email && window.mixpanel) {
      window.mixpanel.register({
        email: user.email,
        loginSource: 'belong-next-external',
        loginTimestamp: new Date().toISOString(),
      });
    }
  };
}

async function logout() {
  try {
    await logoutService();

    clearJwtToken();
    clearOctaneJwtToken();

    destroyCookie(null, JWT_TOKEN.name, { path: '/' });
    destroyCookie(null, REFRESH_TOKEN.name, { path: '/' });
    destroyCookie(null, REFERRAL_USER.name, { path: '/' });
    destroyCookie(null, REFERRAL_ID.name, { path: '/' });

    window.Intercom?.('shutdown');
    shutdownOneLiveChat();
  } catch (error) {
    console.error('Error when logging out', error);
  }
}

export function useStartOTPFlow() {
  const { mutateAsync } = useMutation(startOTPFlowService);

  return {
    startOTPFlow: mutateAsync,
  };
}

export function useVerifyOTPCode() {
  const { mutateAsync } = useMutation(verifyOTPCodeService);

  return {
    verifyOTPCode: mutateAsync,
  };
}
